import { type ReactNode, useEffect } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { SidePanel } from '@vta/ui';
import { HeaderBar } from './HeaderBar.tsx';
import { FooterStrip } from './FooterStrip.tsx';
import { MobileSheet } from './MobileSheet.tsx';
import { useSelectedTeamStore } from '../../stores/selected-team-store.ts';
import { useReducedMotion } from '../../lib/useReducedMotion.ts';
import { useMediaQuery } from '../../lib/useMediaQuery.ts';
import { useGlobeStore } from '../../stores/globe-store.ts';
import { TeamDrawerBody } from '../../features/teams/TeamDrawerBody.tsx';
import { CityDrawerBody } from '../../features/cities/CityDrawerBody.tsx';

/**
 * Persistent layout (PRD §9.2), in two registers:
 *
 * Atlas mode (`/` only) — the globe is full-bleed edge to edge; header and
 * footer float over it on gradient scrims ("the globe is the hero"):
 *   ┌───────────── HeaderBar (floating, glass) ─────────────┐
 *   │                                                       │
 *   │        <Outlet/> = globe, absolute inset-0            │  Drawer →
 *   │                                                       │
 *   └───────────── FooterStrip (floating, glass) ───────────┘
 *
 * Page mode (every other route) — standard document flow with sticky header,
 * scrolling content, and a static footer.
 *
 * Why `h-dvh`/`min-h-dvh` not `h-screen`: dvh respects mobile browsers'
 * dynamic viewport (URL bar collapse); without it the globe gets cropped by
 * ~80px on iOS Safari.
 */
export interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const setReduceMotion = useGlobeStore((s) => s.setReduceMotion);
  const reduce = useReducedMotion();
  useEffect(() => setReduceMotion(reduce), [reduce, setReduceMotion]);

  const isOpen = useSelectedTeamStore((s) => s.isOpen);
  const close = useSelectedTeamStore((s) => s.close);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const atlasMode = pathname === '/';

  if (atlasMode) {
    return (
      <div className="relative h-dvh overflow-hidden bg-bg-sunken text-fg">
        <main id="main" className="absolute inset-0">
          {children}
        </main>
        <HeaderBar floating />
        <FooterStrip floating />
        <DesktopDrawerSlot isOpen={isOpen} onClose={close} />
        <MobileSheet />
      </div>
    );
  }

  return (
    <div className="relative flex min-h-dvh flex-col bg-bg-base text-fg">
      <HeaderBar />

      {/*
        `flex-1` ensures the content slot stretches between header and footer.
        `relative` so absolutely-positioned children can anchor against it.
      */}
      <main id="main" className="relative flex-1 overflow-hidden">
        {children}
      </main>

      <FooterStrip />

      {/*
        Drawer is gated by a runtime media-query rather than a CSS-only
        `hidden` class because Vaul portals its overlay to <body> and the
        wrong one would catch backdrop clicks even when visually hidden.
      */}
      <DesktopDrawerSlot isOpen={isOpen} onClose={close} />
      <MobileSheet />
    </div>
  );
}

function DesktopDrawerSlot({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  if (!isDesktop) return null;
  // A plain fixed panel (NOT Vaul): it floats over the globe but only occupies
  // its own 420px box, so the globe stays draggable/zoomable behind it. Vaul's
  // drag-gesture listeners would otherwise swallow the globe's pointer events.
  return (
    <SidePanel open={isOpen} onClose={onClose} label="Selection details">
      {isOpen ? <SelectionDrawerBody /> : null}
    </SidePanel>
  );
}

/**
 * Drawer body for the selected team / cluster.
 *
 * Switches on `selection.kind`:
 *   - 'team' → TeamDrawerBody (fetches /api/teams/:teamNumber)
 *   - 'city' → CityDrawerBody (fetches /api/cities/:slug)
 */
function SelectionDrawerBody() {
  const selection = useSelectedTeamStore((s) => s.selection);
  if (selection.kind === 'team') {
    return <TeamDrawerBody teamNumber={selection.teamNumber} />;
  }
  if (selection.kind === 'city') {
    return (
      <CityDrawerBody
        citySlug={selection.citySlug}
        fallbackCity={selection.city}
        fallbackCountry={selection.country}
      />
    );
  }
  return null;
}
